import { loadMyApplications, withdrawLeaseApplication } from 'api/services/applicationService'
import useAuthService from 'api/services/authService'
import useFavoritesService from 'api/services/favoritesService'
import { loadOwnerDashboardSummary } from 'api/services/ownerListingManagerService'
import usePropertyService from 'api/services/propertyService'
import useSearchAlertsService from 'api/services/searchAlertsService'
import useUserService from 'api/services/userService'
import { loadMyVerification } from 'api/services/verificationService'
import { getAuthUserSubject } from 'api/utils/AuthManager'
import PageLoading from 'components/common/loading/PageLoading'
import ListingValidation from 'components/listings/ListingVerification'
import MySpaceAlertsSection from 'components/myAccount/MySpaceAlertsSection'
import MySpaceApplicationsSection from 'components/myAccount/MySpaceApplicationsSection'
import MySpaceFavoritesSection, {
  OwnerListingFavoriteRow,
} from 'components/myAccount/MySpaceFavoritesSection'
import {
  getFirstRegisteredProperty,
  getListingStatusKey,
  propertyAddressToAddressData,
  resolveProfileAddress,
} from 'components/myAccount/mySpaceUtils'
import OwnerDashboard from 'components/myAccount/OwnerDashboard'
import SeekerDashboard from 'components/myAccount/SeekerDashboard'
import SecurityVerificationModal from 'components/verification/SecurityVerificationModal'
import { submitTenantVerificationDocuments } from 'components/verification/submitTenantVerification'
import { needsOwnerPersonalDocs } from 'components/verification/verificationUtils'
import { AddressData } from 'newTypes/location'
import { Unit } from 'newTypes/unit'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { VerificationStatus } from 'types/property'

const AccountPage = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { loadUser } = useUserService()
  const { loadMyProperties, loadListedProperty, verifyProperty } = usePropertyService()
  const { loadFavorites, addFavorite } = useFavoritesService()
  const { loadMyAlerts } = useSearchAlertsService()
  const favoritesSectionRef = useRef<HTMLElement>(null)
  const applicationsSectionRef = useRef<HTMLElement>(null)
  const alertsSectionRef = useRef<HTMLElement>(null)
  const [removingFavoriteId, setRemovingFavoriteId] = useState<string | null>(null)
  const [userData, setUserData] = useState<Record<string, unknown> | null>(null)
  const [initialData, setInitialData] = useState<Record<string, unknown> | null>(null)
  const [inVerification, setInVerification] = useState<string | null>(null)
  const [showTenantVerificationModal, setShowTenantVerificationModal] = useState(false)
  const [isSubmittingTenantVerification, setIsSubmittingTenantVerification] =
    useState(false)
  const { isAuthenticated, user: authUser, token } = useAuthService()
  const currentUserId = getAuthUserSubject(authUser)
  const canFetchAccountData = isAuthenticated && Boolean(currentUserId) && Boolean(token)
  const {
    role,
    hasListing,
    emailValidated,
    refresh: refreshPermissions,
  } = usePermissions()
  const queryClient = useQueryClient()
  const [ownerHealed, setOwnerHealed] = useState(false)
  const [emailGateRequired, setEmailGateRequired] = useState(false)
  const focusListingId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return (params.get('listingId') || '').trim() || null
  }, [location.search])

  // After create-ad, client role can lag as "tenant" while DB is already owner.
  // Run once per auth session — do not depend on role/hasListing (refresh updates
  // those and would re-fire endlessly when the permissions request fails).
  useEffect(() => {
    if (!canFetchAccountData) return
    let cancelled = false
    void refreshPermissions().then(payload => {
      if (cancelled) return
      if (payload?.role === 'owner' || payload?.hasListing) {
        setOwnerHealed(true)
      }
      const isPublisher =
        payload?.role === 'owner' || Boolean(payload?.hasListing)
      if (isPublisher && payload && !payload.emailValidated) {
        setEmailGateRequired(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [canFetchAccountData, refreshPermissions])

  const showOwnerManagement =
    role === 'owner' || role === 'admin' || ownerHealed || hasListing

  const needsEmailVerification =
    emailGateRequired ||
    (showOwnerManagement && role !== 'admin' && !emailValidated)

  const {
    data: fetchedUser,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
  } = useQuery(['loadUser', currentUserId], () => loadUser(), {
    enabled: canFetchAccountData,
    onSuccess: data => {
      setUserData(data)
      setInitialData(data)
    },
  })

  const {
    data: myPropertiesRaw,
    isLoading: isLoadingProperties,
    isError: isPropertiesError,
    error: propertiesError,
  } = useQuery(['myProperties', currentUserId], loadMyProperties, {
    enabled: canFetchAccountData && !needsEmailVerification,
    retry: false,
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code
      const errRole = (err as { role?: string })?.role
      if (code === 'email_not_verified') {
        setOwnerHealed(true)
        setEmailGateRequired(true)
        return
      }
      if (errRole === 'owner') setOwnerHealed(true)
    },
    onSuccess: data => {
      if (Array.isArray(data) && data.length > 0) {
        setOwnerHealed(true)
        void refreshPermissions()
      }
    },
  })

  const propertiesBlockedByEmail =
    isPropertiesError &&
    (propertiesError as { code?: string } | null)?.code === 'email_not_verified'

  // List comes from the backend only. Drop obvious orphan rows (user_fk 0/empty).
  const myProperties = useMemo(() => {
    if (!Array.isArray(myPropertiesRaw)) return []
    return myPropertiesRaw.filter((property: { userFk?: unknown; user_fk?: unknown }) => {
      const ownerFk = property?.userFk ?? property?.user_fk
      if (ownerFk == null || ownerFk === '') return true
      const owner = String(ownerFk).trim()
      return owner !== '' && owner !== '0'
    })
  }, [myPropertiesRaw])

  // After publish → verify-email → /account?listingId=…, scroll to Mes annonces.
  useEffect(() => {
    if (!focusListingId || isLoadingProperties) return
    const timer = window.setTimeout(() => {
      document
        .getElementById('my-space-listings')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    return () => window.clearTimeout(timer)
  }, [focusListingId, isLoadingProperties, myProperties])

  const firstPropertyAddress = useMemo((): AddressData | null => {
    const firstListing = getFirstRegisteredProperty(myProperties)
    return propertyAddressToAddressData(firstListing?.address)
  }, [myProperties])

  const resolvedProfileSource = useMemo(() => {
    if (userData) return userData
    if (fetchedUser) return fetchedUser as Record<string, unknown>
    if (authUser && typeof authUser === 'object') return authUser as Record<string, unknown>
    return null
  }, [userData, fetchedUser, authUser])

  const profileUserData = useMemo(() => {
    if (!resolvedProfileSource) return null
    const resolvedAddress = resolveProfileAddress(
      initialData?.address,
      resolvedProfileSource.address,
      firstPropertyAddress
    )
    return {
      ...resolvedProfileSource,
      address: resolvedAddress ?? resolvedProfileSource.address,
    }
  }, [resolvedProfileSource, initialData?.address, firstPropertyAddress])

  const isProfileLoading =
    canFetchAccountData &&
    !resolvedProfileSource &&
    (isLoadingUser || isFetchingUser)

  useEffect(() => {
    if (userData || fetchedUser) return
    if (!resolvedProfileSource) return
    setUserData(resolvedProfileSource)
    setInitialData(resolvedProfileSource)
  }, [userData, fetchedUser, resolvedProfileSource])

  const { mutateAsync: verifyPropertyMutation, isLoading: isVerifying } =
    useMutation(verifyProperty)

  const { data: applications, isLoading: isLoadingApplications } = useQuery(
    ['myApplications', currentUserId],
    loadMyApplications,
    { enabled: canFetchAccountData }
  )

  const { data: identityVerification } = useQuery(
    ['myVerification', currentUserId],
    loadMyVerification,
    { enabled: canFetchAccountData }
  )

  const { data: favorites = [], isLoading: isLoadingFavorites } = useQuery(
    ['loadFavorites', currentUserId],
    loadFavorites,
    { enabled: canFetchAccountData }
  )

  const { data: searchAlerts, isLoading: isLoadingAlerts } = useQuery(
    ['searchAlerts', currentUserId],
    loadMyAlerts,
    { enabled: canFetchAccountData }
  )

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '')
    if (!hash) return

    if (hash === 'my-space-profile') {
      navigate('/profile', { replace: true })
      return
    }

    const timer = window.setTimeout(() => {
      const el = document.getElementById(hash)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)

    return () => window.clearTimeout(timer)
  }, [
    searchAlerts,
    isLoadingAlerts,
    isProfileLoading,
    isLoadingProperties,
    isLoadingFavorites,
    isLoadingApplications,
    location.hash,
    navigate,
  ])

  const { mutateAsync: favoriteMutation } = useMutation(addFavorite)

  const {
    data: ownerDashboard,
    isLoading: isLoadingOwnerDashboard,
  } = useQuery(['ownerDashboardSummary', currentUserId], loadOwnerDashboardSummary, {
    enabled: showOwnerManagement && canFetchAccountData && !needsEmailVerification,
    staleTime: 60_000,
    retry: 1,
    onError: (err: unknown) => {
      if ((err as { code?: string })?.code === 'email_not_verified') {
        setOwnerHealed(true)
        setEmailGateRequired(true)
      }
    },
  })

  const favoritesCount = showOwnerManagement
    ? ownerDashboard?.metrics?.favorites ?? 0
    : favorites.length
  const applicationsStatCount = showOwnerManagement
    ? ownerDashboard?.metrics?.applications ?? 0
    : applications?.length ?? 0

  const statsLoading = showOwnerManagement
    ? isLoadingOwnerDashboard
    : isLoadingFavorites
  const favoritesSectionLoading = showOwnerManagement
    ? isLoadingOwnerDashboard
    : isLoadingFavorites

  const ownerListingFavorites = useMemo((): OwnerListingFavoriteRow[] => {
    if (!showOwnerManagement || !myProperties?.length || !ownerDashboard) return []

    return (ownerDashboard.listingFavorites ?? [])
      .map(row => {
        const listing = myProperties.find(
          (property: Unit) => String(property.id) === String(row.listingId)
        )
        if (!listing || row.favoritesCount <= 0) return null
        // Only published listings: pending/unverified ads must not appear in owner favorites.
        if (getListingStatusKey(listing) !== 'active') return null
        return { listing, favoritesCount: row.favoritesCount }
      })
      .filter((row): row is OwnerListingFavoriteRow => row !== null)
  }, [showOwnerManagement, myProperties, ownerDashboard])

  const ownerReceivedApplications = ownerDashboard?.applications ?? []
  const ownerVisitsCount = ownerDashboard?.metrics?.visitsRequests ?? 0
  const ownerPendingVisitCount =
    ownerDashboard?.metrics?.pendingVisitRequests ?? 0
  const firstPendingVisitListingId =
    ownerDashboard?.pendingVisitsByListing?.[0]?.listingId != null
      ? String(ownerDashboard.pendingVisitsByListing[0].listingId)
      : null

  const scrollToFavorites = () => {
    favoritesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const scrollToApplications = () => {
    applicationsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleRemoveFavorite = async (propertyId: string) => {
    setRemovingFavoriteId(propertyId)
    try {
      await favoriteMutation(propertyId)
      await queryClient.invalidateQueries(['loadFavorites', currentUserId])
    } catch {
      toast.error(String(t('account.mySpace.removeFavoriteFailed')))
    } finally {
      setRemovingFavoriteId(null)
    }
  }

  const { mutateAsync: withdrawApplicationMutation, isLoading: isWithdrawingApplication } =
    useMutation(withdrawLeaseApplication)

  const handleWithdrawApplication = async (applicationId: string | number) => {
    try {
      await withdrawApplicationMutation(applicationId)
      toast.success(String(t('account.mySpace.withdrawSuccess')))
      await queryClient.invalidateQueries(['myApplications', currentUserId])
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t('account.mySpace.withdrawFailed')
      toast.error(String(message))
    }
  }

  const handleOpenVerify = (listingId: string) => {
    setInVerification(listingId)
  }

  const handleVerify = async (data: {
    files: {
      leaseAgreement?: string[]
      personalId?: string[]
      selfieWithId?: string[]
    }
    propertyId: string | null
  }) => {
    try {
      const response = await verifyPropertyMutation(data)

      if (response.success) {
        toast.success(String(t('account.mySpace.verifySuccess')))
        setInVerification(null)
        if (data.propertyId) {
          queryClient.setQueryData<Unit[] | undefined>(
            ['myProperties', currentUserId],
            old =>
              old?.map(property =>
                property.id === data.propertyId
                  ? { ...property, verified: VerificationStatus.Pending }
                  : property
              )
          )
        }
        await queryClient.invalidateQueries(['myProperties', currentUserId])
        await queryClient.invalidateQueries(['myVerification', currentUserId])
      } else {
        toast.error(String(t('account.mySpace.verifyFailed')))
      }
    } catch {
      toast.error(String(t('account.mySpace.verifyFailed')))
    }
  }

  const ownerNeedsPersonalDocs = needsOwnerPersonalDocs(identityVerification)

  const applicationPropertyIds = useMemo(() => {
    if (showOwnerManagement) return [] as string[]
    const ids = (applications ?? [])
      .map(app => (app.property_fk != null ? String(app.property_fk) : ''))
      .filter(Boolean)
    return Array.from(new Set(ids))
  }, [applications, showOwnerManagement])

  const applicationListingQueries = useQueries(
    applicationPropertyIds.map(propertyId => ({
      queryKey: ['seekerApplicationListing', propertyId],
      queryFn: () => loadListedProperty(propertyId),
      enabled: !showOwnerManagement && Boolean(propertyId),
      staleTime: 60_000,
    }))
  )

  const listingById = useMemo(() => {
    const map: Record<string, Unit | undefined> = {}
    applicationPropertyIds.forEach((propertyId, index) => {
      map[propertyId] = applicationListingQueries[index]?.data as Unit | undefined
    })
    return map
  }, [applicationPropertyIds, applicationListingQueries])

  const handleTenantVerificationSubmit = async (
    files: Parameters<typeof submitTenantVerificationDocuments>[0]
  ) => {
    setIsSubmittingTenantVerification(true)
    try {
      await submitTenantVerificationDocuments(files)
      await queryClient.invalidateQueries(['myVerification', currentUserId])
      await queryClient.invalidateQueries(['myApplications', currentUserId])
      setShowTenantVerificationModal(false)
      toast.success(String(t('account.mySpace.verifySuccess')))
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t('account.mySpace.verifyFailed')
      toast.error(String(message))
    } finally {
      setIsSubmittingTenantVerification(false)
    }
  }

  const ownerFirstName = useMemo(() => {
    const profileRecord = profileUserData as Record<string, unknown> | null
    const fromProfile =
      profileRecord && typeof profileRecord.name === 'string'
        ? profileRecord.name
        : ''
    const fromAuth =
      typeof authUser === 'object' &&
      authUser &&
      'displayName' in authUser &&
      typeof (authUser as { displayName?: string }).displayName === 'string'
        ? (authUser as { displayName: string }).displayName
        : ''
    const raw = fromProfile || fromAuth
    return raw.trim().split(/\s+/)[0] || ''
  }, [profileUserData, authUser])

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />
  }

  if (needsEmailVerification || propertiesBlockedByEmail) {
    return (
      <Navigate
        to="/verify-email"
        replace
        state={{
          from: location,
          next: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-imovWhiteShadow to-white py-6 md:py-10">
      <div className="mx-auto max-w-6xl px-4">
        {isProfileLoading ? (
          <PageLoading minHeightClassName="min-h-[calc(100vh-220px)]" />
        ) : showOwnerManagement ? (
          <div className="flex flex-col gap-8">
            <OwnerDashboard
              firstName={ownerFirstName}
              listings={myProperties}
              isLoadingListings={isLoadingProperties}
              favoritesCount={favoritesCount}
              applicationsCount={applicationsStatCount}
              visitsCount={ownerVisitsCount}
              statsLoading={statsLoading}
              pendingVisitCount={ownerPendingVisitCount}
              firstPendingVisitListingId={firstPendingVisitListingId}
              onOpenVerify={handleOpenVerify}
              onScrollToFavorites={scrollToFavorites}
              onScrollToApplications={scrollToApplications}
              favoritesSlot={
                <MySpaceFavoritesSection
                  ref={favoritesSectionRef}
                  isOwnerView
                  savedFavorites={favorites}
                  ownerListingFavorites={ownerListingFavorites}
                  isLoading={favoritesSectionLoading}
                />
              }
              applicationsSlot={
                <MySpaceApplicationsSection
                  ref={applicationsSectionRef}
                  applications={ownerReceivedApplications}
                  isLoading={isLoadingOwnerDashboard}
                  isOwnerView
                  onWithdraw={handleWithdrawApplication}
                  isWithdrawing={isWithdrawingApplication}
                />
              }
            />

            <ListingValidation
              isLoading={isVerifying}
              verification={inVerification}
              needsPersonalDocs={ownerNeedsPersonalDocs}
              handleClose={() => setInVerification(null)}
              handleVerify={handleVerify}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <SeekerDashboard
              firstName={ownerFirstName}
              applications={applications}
              isLoadingApplications={isLoadingApplications}
              listingById={listingById}
              identityVerification={identityVerification}
              profile={{
                name:
                  typeof (profileUserData as Record<string, unknown> | null)?.name ===
                  'string'
                    ? String((profileUserData as Record<string, unknown>).name)
                    : '',
                email:
                  typeof (profileUserData as Record<string, unknown> | null)?.email ===
                  'string'
                    ? String((profileUserData as Record<string, unknown>).email)
                    : '',
                phone:
                  typeof (profileUserData as Record<string, unknown> | null)?.phone ===
                  'string'
                    ? String((profileUserData as Record<string, unknown>).phone)
                    : '',
              }}
              onContinueVerification={() => setShowTenantVerificationModal(true)}
              onCompleteProfile={() => {
                const incompleteIdentity =
                  !identityVerification ||
                  identityVerification.status === 'profile_incomplete' ||
                  identityVerification.status === 'identity_pending' ||
                  identityVerification.status === 'identity_correction_requested'
                if (incompleteIdentity) {
                  setShowTenantVerificationModal(true)
                  return
                }
                navigate('/profile')
              }}
              favoritesSlot={
                <MySpaceFavoritesSection
                  ref={favoritesSectionRef}
                  isOwnerView={false}
                  savedFavorites={favorites}
                  ownerListingFavorites={ownerListingFavorites}
                  isLoading={favoritesSectionLoading}
                  onRemoveFavorite={handleRemoveFavorite}
                  removingFavoriteId={removingFavoriteId}
                />
              }
              alertsSlot={
                <MySpaceAlertsSection
                  ref={alertsSectionRef}
                  alerts={searchAlerts}
                  isLoading={isLoadingAlerts}
                  onChanged={() => {
                    void queryClient.invalidateQueries(['searchAlerts', currentUserId])
                  }}
                />
              }
            />

            <SecurityVerificationModal
              isOpen={showTenantVerificationModal}
              mode="tenant"
              isLoading={isSubmittingTenantVerification}
              onClose={() => setShowTenantVerificationModal(false)}
              onSubmit={files => void handleTenantVerificationSubmit(files)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default AccountPage
